// src/context/AuthContext.js
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../utils/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user,    setUser]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast,   setToast]   = useState(null);

  const showToast = useCallback((title, body = '', type = 'success') => {
    setToast({ title, body, type });
    setTimeout(() => setToast(null), 3500);
  }, []);

  // Auto-login from stored token
  useEffect(() => {
    const token = localStorage.getItem('codrion_token');
    if (!token) { setLoading(false); return; }
    authAPI.me()
      .then(res => setUser(res.data.user))
      .catch(() => localStorage.removeItem('codrion_token'))
      .finally(() => setLoading(false));
  }, []);

  const register = async (username, email, password) => {
    const { data } = await authAPI.register(username, email, password);
    localStorage.setItem('codrion_token', data.token);
    setUser(data.user);
    showToast('Welcome to Codrion! 🎉', 'Your journey begins now!', 'success');
    return data;
  };

  const login = async (email, password) => {
    const { data } = await authAPI.login(email, password);
    localStorage.setItem('codrion_token', data.token);
    setUser(data.user);
    showToast('Welcome back!', `${data.user.username} — ${data.user.streak || 0} day streak!`);
    return data;
  };

  const logout = useCallback(async () => {
    try { await authAPI.logout(); } catch { /* ignore network errors on logout */ }
    localStorage.removeItem('codrion_token');
    setUser(null);
  }, []);

  const claimDailyReward = async () => {
    const { data } = await authAPI.daily();
    setUser(prev => ({ ...prev, coins: data.newCoins, xp: data.newXP, streak: data.streak }));
    showToast(`Daily Reward! +${data.coinsReward} coins`,
              `+${data.xpReward} XP | ${data.streak} day streak 🔥`, 'gold');
    return data;
  };

  const updateUser = useCallback(updates =>
    setUser(prev => prev ? { ...prev, ...updates } : prev), []);

  return (
    <AuthContext.Provider value={{ user, loading, toast, register, login, logout,
                                   claimDailyReward, updateUser, showToast }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
